import { CdkStepper } from '@angular/cdk/stepper';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import {
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { MatAnchor, MatButton } from '@angular/material/button';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatOption } from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle
} from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import {
  MatError,
  MatFormField,
  MatLabel,
  MatPrefix,
  MatSuffix
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatSelect } from '@angular/material/select';
import { MatStep, MatStepper, MatStepperIcon } from '@angular/material/stepper';
import { format, parse } from 'date-fns';
import moment from 'moment';
import {
  NgxMatTimepickerComponent,
  NgxMatTimepickerModule
} from 'ngx-mat-timepicker';
import { ToastrService } from 'ngx-toastr';
import { of, Subject } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  startWith,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';
import { Booking } from '../../../models/booking';
import { Docket } from '../../../models/index';
import {
  DeliverableItem,
  DeliveryListItem
} from '../../deliveries/docket-delivery-list/deliverable-service.interface';
import { AddCurrencyPrefixDirective } from '../../directives/add-currency-prefix.directive';
import { TestIdDirective } from '../../directives/test-id.directive';
import { AddressSearchFieldComponent } from '../../general/address-search-field/address-search-field.component';
import { BookingItemNamePipe } from '../../pipes/booking-item-name.pipe';
import { CustomerAddressPipe } from '../../pipes/customer-address.pipe';
import { DeliveryItemNamePipe } from '../../pipes/delivery-item-name.pipe';
import { ConfigService } from '../../services/config.service';
import { DeliveryService } from '../../services/delivery.service';
import { DeliveryEstimateComponent } from './delivery-estimate/delivery-estimate.component';

export interface DeliveryDialogData {
  deliverableId: number;
  deliverableType: string;
  items: DeliverableItem[];
  customerAddress: string;
  currency: string;
  currencyRate: number;
  delivery?: DeliveryListItem;
}

@Component({
  selector: 'delivery-dialog',
  templateUrl: './delivery-dialog.component.html',
  styleUrls: ['./delivery-dialog.component.css'],
  providers: [
    DeliveryService,
    CustomerAddressPipe,
    CdkStepper,
    BookingItemNamePipe,
    DeliveryItemNamePipe
  ],
  standalone: true,
  imports: [
    NgIf,
    MatDialogTitle,
    MatDialogContent,
    ReactiveFormsModule,
    MatStepper,
    MatStepperIcon,
    MatIcon,
    MatStep,
    MatCheckbox,
    FormsModule,
    NgFor,
    MatRadioGroup,
    MatRadioButton,
    MatFormField,
    MatLabel,
    MatInput,
    MatSelect,
    MatOption,
    MatAnchor,
    AddressSearchFieldComponent,
    DeliveryEstimateComponent,
    MatPrefix,
    AddCurrencyPrefixDirective,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatDialogActions,
    MatButton,
    AsyncPipe,
    MatError,
    NgxMatTimepickerModule,
    TestIdDirective
  ]
})
export class DeliveryDialogComponent implements OnInit, OnDestroy {
  deliverableId: number;
  deliverableType: string;
  delivery: DeliveryListItem;
  deliverable: Booking | Docket;

  @Input() set type(value: 'delivery' | 'collection') {
    this.deliveryForm.controls['type'].patchValue(value);
  }

  deliveryForm = this.formBuilder.group({
    type: new FormControl<string>('delivery', Validators.required),
    location: new FormControl<string>(''),
    address: new FormControl<string>(null, Validators.required),
    external_fulfillment: new FormControl<boolean>(false),
    fulfillment_provider: new FormControl<string>(null),
    notes: new FormControl<string>(null),
    charge: new FormControl<number>(null, Validators.required),
    due_date: new UntypedFormControl(null, Validators.required),
    due_time: new UntypedFormControl(null, Validators.required),
    second_due_date: new UntypedFormControl(null),
    second_due_time: new UntypedFormControl(null),
    allocated_to: new FormControl<number | null>(null)
  });

  private ngUnsubscribe = new Subject<void>();
  useCustomerAddress = false;
  potentialItems: DeliverableItem[];
  selectAll = false;
  @ViewChild(MatStepper, { static: true })
  matHorizontalStepper: MatStepper;
  @ViewChild(MatStep, { static: true }) itemsStep: MatStep;
  addressType = 'search';
  assignableUsers$ = this.configService.allUsers.pipe(
    map((users) => {
      return users.filter((user) => [999, 101].includes(user.id) === false);
    })
  );

  constructor(
    public dialogRef: MatDialogRef<DeliveryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DeliveryDialogData,
    private deliveryService: DeliveryService,
    private formBuilder: UntypedFormBuilder,
    private toastService: ToastrService,
    private configService: ConfigService
  ) {
    if (data.deliverableId) {
      this.deliverableId = data.deliverableId;
    }
    if (data.deliverableType) {
      this.deliverableType = data.deliverableType;
    }
    if (data.items) {
      this.potentialItems = data.items;
      const itemsFormArray = new UntypedFormArray([]);
      this.deliveryForm.addControl('items', itemsFormArray);
      this.potentialItems.forEach((item) => {
        itemsFormArray.push(
          this.formBuilder.group({
            id: new FormControl(item.deliverable_item_id),
            deliver: new FormControl(item.on_job),
            description: new FormControl(item.description)
          })
        );
      });
    }

    this.deliveryForm.controls['type'].valueChanges.subscribe((type) => {
      if (type === 'both') {
        this.deliveryForm.controls['second_due_date'].setValidators(
          Validators.required
        );
        this.deliveryForm.controls['second_due_time'].setValidators(
          Validators.required
        );
      } else {
        this.deliveryForm.controls['second_due_date'].clearValidators();
        this.deliveryForm.controls['second_due_time'].clearValidators();
      }
      this.deliveryForm.controls['second_due_date'].updateValueAndValidity();
      this.deliveryForm.controls['second_due_time'].updateValueAndValidity();
    });

    if (data.delivery) {
      this.delivery = data.delivery;
      const { due, google_place_id, location, ...bulk } = data.delivery;
      this.deliveryForm.patchValue(bulk);
      this.deliveryForm.controls['due_date'].patchValue(moment(due));
      this.deliveryForm.controls['due_time'].patchValue(
        format(new Date(due), 'HH:mm')
      );
      if (!google_place_id) {
        this.deliveryForm.controls['location'].patchValue(location);
        this.setAddressType('typed');
      } else {
        this.deliveryForm.controls['address'].patchValue({
          address: location,
          place_id: google_place_id
        });
        this.setAddressType('search');
      }
    } else {
      this.setDefaultCharge();
    }
  }

  private setDefaultCharge() {
    this.configService.userConfig
      .pipe(
        takeUntil(this.ngUnsubscribe),
        map(
          ({
            default_delivery_price: delivery,
            default_collection_price: collection
          }) => ({ delivery, collection })
        ),
        mergeMap((value) =>
          this.deliveryForm.controls['type'].valueChanges.pipe(
            // start with the default
            startWith(this.deliveryForm.controls['type'].value),
            // map to the type property on config - delivery or collection
            map((type: string) => {
              if (type === 'both') {
                return value.delivery;
              }
              return value[type.toLocaleLowerCase()] ?? '';
            })
          )
        )
      )
      .subscribe((charge) => {
        // patch the charge value
        this.deliveryForm.patchValue({ charge });
      });
  }

  ngOnInit() {
    this.itemSelectionChange();
  }

  submit() {
    if (!this.delivery) {
      this.createJobs();
      return;
    }
    this.updateExistingDelivery();
  }

  createJobs() {
    const { due_date, due_time, type, second_due_date, second_due_time } =
      this.deliveryForm.value;
    const dueDateTime = parse(due_time, 'HH:mm', due_date.toDate());
    if (type === 'both') {
      const secondDueDateTime = parse(
        second_due_time,
        'HH:mm',
        second_due_date.toDate()
      );

      this.createNewDelivery('delivery', dueDateTime)
        .pipe(
          catchError((error) => {
            this.toastService.error(
              'Error creating delivery, no transport jobs added',
              'Error creating delivery'
            );
            console.log(error);
            return of(null);
          }),
          take(1),
          filter(Boolean),
          mergeMap((delivery) =>
            this.createNewDelivery('collection', secondDueDateTime).pipe(
              catchError((error) => {
                this.toastService.error(
                  'Error creating collection, delivery transport job added successfully',
                  'Error creating collection'
                );
                console.log(error);
                return of(null);
              }),
              take(1),
              filter(Boolean),
              map((collection) => [delivery, collection])
            )
          )
        )
        .subscribe((response) => {
          this.dialogRef.close(response);
        });
      return;
    }
    this.createNewDelivery(type, dueDateTime).subscribe((response) => {
      this.dialogRef.close([response]);
    });
  }

  private buildDeliveryPayload(
    type: 'delivery' | 'collection',
    dueDateTime: Date | string,
    isUpdate: boolean = false
  ) {
    const {
      items,
      address,
      due_date,
      due_time,
      second_due_date,
      second_due_time,
      ...delivery
    } = this.deliveryForm.value;

    // Prepare items array
    delivery.items = items
      .filter((item) => item.deliver)
      .map((item) => {
        const base = {
          deliverable_item_id: item.id,
          deliverable_item_type:
            this.deliverableType === 'docket' ? 'hireItem' : 'bookingItem'
        };
        // Only add delivery_id for updates
        return isUpdate ? { ...base, delivery_id: this.delivery.id } : base;
      });

    // Set IDs
    delivery.id = isUpdate ? this.delivery.id : undefined;
    delivery.deliverable_id = this.data.deliverableId;
    delivery.deliverable_type = this.deliverableType;
    delivery.due = dueDateTime;
    delivery.charge = delivery.charge / this.data.currencyRate;
    delivery.type = type;

    // Set address fields
    if (address?.place_id) {
      delivery.location = address.address;
      delivery.google_place_id = address.place_id;
    } else {
      delivery.google_place_id = null;
    }

    return delivery;
  }

  private createNewDelivery(
    type: 'delivery' | 'collection',
    dueDateTime: Date
  ) {
    const payload = this.buildDeliveryPayload(type, dueDateTime, false);
    return this.deliveryService
      .create(payload)
      .pipe(takeUntil(this.ngUnsubscribe));
  }

  private updateExistingDelivery() {
    const { due_date, due_time } = this.deliveryForm.value;
    const dueDateTime = parse(
      due_time,
      'HH:mm',
      due_date.toDate()
    ).toISOString();
    const payload = this.buildDeliveryPayload(
      this.deliveryForm.value.type,
      dueDateTime,
      true
    );

    this.deliveryService
      .update(this.delivery.id, payload)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((response) => {
        this.dialogRef.close(true);
      });
  }

  customerAddressToggle(event: MatCheckboxChange) {
    if (event.checked) {
      this.deliveryForm.controls['location'].patchValue(
        this.data.customerAddress
      );
    }
  }

  itemSelectionChange() {
    const selections = this.deliveryForm
      .get('items')
      .value.map((item) => item.deliver);

    this.selectAll = selections.every((item) => item);
    this.itemsStep.completed = selections.some((item) => item);
  }

  toggleAllItems(event: MatCheckboxChange) {
    (this.deliveryForm.get('items') as UntypedFormArray).controls.forEach(
      (itemGroup, i) => {
        (
          (this.deliveryForm.get('items') as UntypedFormArray).controls[
            i
          ] as UntypedFormGroup
        ).controls['deliver'].patchValue(event.checked);
      }
    );
    this.itemSelectionChange();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  setAddressType(search: string) {
    this.addressType = search;
    if (search === 'search') {
      this.deliveryForm.controls['location'].removeValidators(
        Validators.required
      );
      this.deliveryForm.controls['address'].setValidators(Validators.required);
    }
    if (search === 'typed') {
      this.deliveryForm.controls['address'].patchValue(null);
      this.deliveryForm.controls['location'].setValidators(Validators.required);
      this.deliveryForm.controls['address'].removeValidators(
        Validators.required
      );
    }
    this.deliveryForm.controls['address'].updateValueAndValidity();
    this.deliveryForm.controls['location'].updateValueAndValidity();
  }

  updateTime(event: any, field: string) {
    this.deliveryForm.get(field).patchValue(event);
  }

  updateTimeEnteredManually(field: string, picker: NgxMatTimepickerComponent) {
    const control = this.deliveryForm.get(field);
    if (!control.value) {
      return;
    }
    // 24 hr patterns to try and parse
    const parsePatterns = ['HH:mm', 'HH.mm', 'H:mm', 'HHmm', 'Hmm', 'HH', 'H'];
    const invalidValue = control.value;
    for (const pattern of parsePatterns) {
      const parsed = parse(invalidValue, pattern, new Date());
      if (parsed.toString() !== 'Invalid Date') {
        control.patchValue(format(parsed, 'HH:mm'));
        return picker.updateTime(control.value);
      }
    }
    control.setErrors({ invalidTime: true });
  }
}
